/**
 * Payment Gate Hook - Manages consistent payment modal state
 * Addresses S-1003 AC1, AC2, AC3, AC4: Modal consistency, dismissal tracking, subscription updates, conflict prevention
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { isPaymentsEnabled, isMvpEntitlementsOff } from '@/lib/flags';
import { PaymentsModal } from '@/components/modals/PaymentsModal';

interface PaymentGateState {
  isModalOpen: boolean;
  currentFeature: string | null;
  currentEntryPoint: string | null;
  dismissedFeatures: Set<string>;
  subscriptionStatus: 'free' | 'premium' | 'loading';
}

interface PaymentGateContextType {
  // State
  state: PaymentGateState;
  
  // Actions
  checkFeatureAccess: (feature: string, entryPoint?: string) => boolean;
  requestPremiumFeature: (feature: string, entryPoint?: string) => void;
  dismissModal: () => void;
  clearDismissals: () => void;
  
  // Subscription management
  updateSubscriptionStatus: (status: 'free' | 'premium') => void;
}

const PaymentGateContext = createContext<PaymentGateContextType | null>(null);

interface PaymentGateProviderProps {
  children: React.ReactNode;
}

export function PaymentGateProvider({ children }: PaymentGateProviderProps) {
  const [state, setState] = useState<PaymentGateState>({
    isModalOpen: false,
    currentFeature: null,
    currentEntryPoint: null,
    dismissedFeatures: new Set(),
    subscriptionStatus: 'loading'
  });

  // Prevent modal conflicts during rapid feature access (AC4)
  const modalLockRef = useRef(false);

  // Initialize subscription status
  useEffect(() => {
    // For now, simulate subscription check
    // TODO: Replace with actual subscription API call when payments are implemented
    const checkSubscription = async () => {
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // For now, all users are free during beta
        setState(prev => ({
          ...prev,
          subscriptionStatus: 'free'
        }));
      } catch (error) {
        console.error('[PaymentGate] Failed to check subscription status:', error);
        setState(prev => ({
          ...prev,
          subscriptionStatus: 'free'
        }));
      }
    };

    checkSubscription();
  }, []);

  // AC3: Update subscription status when it changes
  const updateSubscriptionStatus = useCallback((status: 'free' | 'premium') => {
    setState(prev => {
      const newState = { ...prev, subscriptionStatus: status };
      
      // If user gains premium access, close modal and clear dismissals
      if (status === 'premium' && prev.subscriptionStatus !== 'premium') {
        newState.isModalOpen = false;
        newState.currentFeature = null;
        newState.currentEntryPoint = null;
        newState.dismissedFeatures = new Set();
        modalLockRef.current = false;
      }
      
      return newState;
    });
  }, []);

  // AC1: Check if user has access to a premium feature
  const checkFeatureAccess = useCallback((feature: string, entryPoint?: string): boolean => {
    // S-2220: If MVP entitlements are off, allow all features (bypass all gating)
    if (isMvpEntitlementsOff()) {
      return true;
    }
    
    // If payments are enabled and user has premium subscription, allow access
    if (isPaymentsEnabled() && state.subscriptionStatus === 'premium') {
      return true;
    }
    
    // If payments are disabled, allow access (beta mode)
    if (!isPaymentsEnabled()) {
      return true;
    }
    
    // Otherwise, block access
    return false;
  }, [state.subscriptionStatus]);

  // AC1, AC2: Request access to a premium feature
  const requestPremiumFeature = useCallback((feature: string, entryPoint: string = 'unknown') => {
    // If user already has access, do nothing
    if (checkFeatureAccess(feature, entryPoint)) {
      return;
    }

    // AC4: Prevent modal conflicts during rapid access
    if (modalLockRef.current) {
      console.log(`[PaymentGate] Modal locked, ignoring request for ${feature}`);
      return;
    }

    // AC2: If feature was dismissed and modal is not open, show modal again
    const featureKey = `${feature}:${entryPoint}`;
    
    setState(prev => ({
      ...prev,
      isModalOpen: true,
      currentFeature: feature,
      currentEntryPoint: entryPoint
    }));

    modalLockRef.current = true;
  }, [checkFeatureAccess]);

  // AC2: Dismiss modal but track dismissal
  const dismissModal = useCallback(() => {
    if (state.currentFeature && state.currentEntryPoint) {
      const featureKey = `${state.currentFeature}:${state.currentEntryPoint}`;
      
      setState(prev => ({
        ...prev,
        isModalOpen: false,
        dismissedFeatures: new Set([...prev.dismissedFeatures, featureKey])
      }));
    } else {
      setState(prev => ({
        ...prev,
        isModalOpen: false
      }));
    }

    // Release modal lock after a brief delay to prevent rapid re-opening
    setTimeout(() => {
      modalLockRef.current = false;
    }, 500);
  }, [state.currentFeature, state.currentEntryPoint]);

  // Clear all dismissals (useful for testing or subscription changes)
  const clearDismissals = useCallback(() => {
    setState(prev => ({
      ...prev,
      dismissedFeatures: new Set()
    }));
  }, []);

  const contextValue: PaymentGateContextType = {
    state,
    checkFeatureAccess,
    requestPremiumFeature,
    dismissModal,
    clearDismissals,
    updateSubscriptionStatus
  };

  return (
    <PaymentGateContext.Provider value={contextValue}>
      {children}
      
      {/* Render modal when needed */}
      <PaymentsModal
        isOpen={state.isModalOpen}
        onClose={dismissModal}
        feature={state.currentFeature || undefined}
        entryPoint={state.currentEntryPoint || undefined}
      />
    </PaymentGateContext.Provider>
  );
}

// Hook to use payment gate functionality
export function usePaymentGate(): PaymentGateContextType {
  const context = useContext(PaymentGateContext);
  
  if (!context) {
    throw new Error('usePaymentGate must be used within a PaymentGateProvider');
  }
  
  return context;
}

// Convenience hook for checking feature access
export function usePremiumFeature(feature: string, entryPoint?: string) {
  const { checkFeatureAccess, requestPremiumFeature } = usePaymentGate();
  
  const hasAccess = checkFeatureAccess(feature, entryPoint);
  
  const requestAccess = useCallback(() => {
    requestPremiumFeature(feature, entryPoint || 'hook');
  }, [requestPremiumFeature, feature, entryPoint]);
  
  return {
    hasAccess,
    requestAccess
  };
}

export default usePaymentGate;
